import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, createElement } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ToastAction } from "@/components/ui/toast";
import { useCurrentOrgId } from '../auth';
import { useToast } from "../use-toast";
import { fetchFilterData, applyFilterGroups } from "@/lib/filterEngine";
import { applyDealSavedFilterToQuery, fetchDealCustomFieldTypesMapCached } from "@/lib/dealsSavedFilterQuery";
import { isSavedFilterFullyServerResolvable } from "@/lib/filters/serverFilterCoverage";
import { NO_OWNER_SENTINEL, applyOwnerFilter, splitOwnerSelection } from "@/lib/filters/ownerFilterQuery";
import { resolveCadenceFilterFromFilterData } from "@/lib/cadenceFilterResolver";
import { shouldApplyDealsListPipelineScope } from "@/constants/dealsList";
import { encodeYmdForTimestamptzColumn } from "@/lib/utils";
import { maybeOpenScheduleAfterDealWon } from "@/lib/activitySchedulePrompt";
import * as Sentry from "@sentry/react";

/** Campos gravados como timestamptz mas editados como data civil (YYYY-MM-DD). */
const DEAL_TIMESTAMPTZ_DATE_FIELDS = ["won_at", "lost_at", "close_date"] as const;

function normalizeDealTimestamptzDateFields<T extends Record<string, unknown>>(patch: T): T {
  const out = { ...patch } as Record<string, unknown>;
  for (const key of DEAL_TIMESTAMPTZ_DATE_FIELDS) {
    const v = out[key];
    if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v.trim())) {
      out[key] = encodeYmdForTimestamptzColumn(v.trim());
    }
  }
  return out as T;
}

/**
 * Mensagem acionável para falha de gravação de negócio. O caso recorrente em
 * produção é o `statement_timeout` de 8s do role `authenticated`: fechar um
 * negócio dispara a cascata de triggers (cancelar atividades, cadências,
 * automações) e, sob concorrência com o dispatcher de cadências, o statement
 * é abortado — nada é gravado e o rep só via "Erro ao mover negócio".
 */
export function describeDealUpdateError(error: unknown): string {
  const code = (error as { code?: string } | null)?.code;
  const message = (error as { message?: string } | null)?.message ?? "";

  if (code === "57014" || /statement timeout|canceling statement/i.test(message)) {
    return "A operação demorou demais e foi cancelada pelo banco. Nada foi salvo — tente novamente em alguns segundos.";
  }
  if (code === "PGRST116") {
    return "Negócio não encontrado nesta organização (ou sem permissão para editá-lo). Recarregue a página e tente de novo.";
  }
  if (error instanceof TypeError && /failed to fetch/i.test(message)) {
    return "Sem conexão com o servidor. Verifique a internet e tente novamente.";
  }
  // Travas do rateio Atticus: o banco manda "codigo: frase"; o vendedor só precisa da frase.
  const rateio = /^(contrato_nao_assinado|pasta_bloqueada|etapa_do_sistema|cs_sem_permissao):\s*(.+)$/s.exec(message);
  if (rateio) {
    return rateio[2].charAt(0).toUpperCase() + rateio[2].slice(1);
  }
  return message || "Erro desconhecido ao salvar o negócio.";
}

interface Tag {
  id: string;
  name: string;
  color: string;
}

export interface Deal {
  id: string;
  org_id: string;
  title: string;
  description?: string;
  amount: number;
  /**
   * Valor total do contrato. Coluna real (não custom field). Mantida pelo trigger
   * `update_deal_value_from_products` quando o negócio tem produtos; digitada à mão no
   * detalhe do negócio quando não tem (caso da maioria dos ganhos).
   */
  contract_total?: number | null;
  stage: string;
  status: 'open' | 'won' | 'lost' | 'deleted';
  deleted_at?: string | null;
  stage_id: string;
  pipeline_id: string;
  probability: number;
  close_date?: string;
  lost_reason?: string;
  loss_reason_id?: string;
  custom_loss_reason?: string;
  lead_id?: string;
  company_id?: string;
  owner_id?: string | null;
  owner?: {
    user_id: string;
    full_name: string | null;
    email: string | null;
    avatar_url: string | null;
  } | null;
  /** True quando owner_id está setado mas o usuário não tem membership ativa na org. */
  owner_inactive?: boolean;
  sdr_responsavel?: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  won_at?: string | null;
  lost_at?: string | null;
  source?: string | null;
  lead_source?: string | null;
  merged_into_deal_id?: string | null;
  merged_at?: string | null;
  merged_by?: string | null;
  custom_data?: Record<string, any>;
  fit_tier?: string | null;
  fit_score?: number | null;
  tags?: Tag[];
  leads?: {
    id: string;
    name: string;
    email: string;
    phone?: string | null;
    source?: string | null;
    utm_source?: string | null;
    utm_medium?: string | null;
    utm_campaign?: string | null;
    utm_term?: string | null;
    utm_content?: string | null;
  } | null;
  companies?: {
    id: string;
    name: string;
  } | null;
  profiles?: {
    full_name: string;
  } | null;
  loss_reasons?: {
    id: string;
    reason_text: string;
  } | null;
  stages?: {
    id: string;
    name: string;
    stage_order: number;
    probability: number;
    stage_type: string | null;
  } | null;
}

// Lightweight select for list view — no activities, trimmed relations
// Os campos de leads/companies precisam cobrir todos os filtros e colunas
// relacionais do registry para permitir que o usuário filtre/exiba dados
// das entidades vinculadas (ex.: companies.faturamento, leads.email, etc).
// `custom_data` é incluído para suportar custom fields/Pipedrive das
// entidades relacionadas no filtro avançado e no seletor de colunas.
const LIST_VIEW_SELECT = `
  *,
  leads!deals_lead_id_fkey(id, name, email, phone, status, source, value, owner_id, created_at, updated_at, last_contact_at, utm_source, utm_medium, utm_campaign, utm_term, utm_content, custom_data),
  companies(id, name, domain, website, industry, size, status, email, phone, instagram, location, address, description, value, faturamento, total_leads, active_leads, converted_leads, owner_id, pipedrive_id, created_at, updated_at, last_contact_at, custom_data),
  profiles!deals_created_by_fkey(full_name),
  stages(id, name, stage_order, probability, stage_type),
  deal_tags(tag_id, tags(id, name, color)),
  loss_reasons(id, reason_text)
`;

// Sort fields that map directly to DB columns
const SERVER_SORT_MAP: Record<string, string> = {
  title: 'title',
  value: 'amount',
  contract_total: 'contract_total',
  close_date: 'close_date',
  won_at: 'won_at',
  lost_at: 'lost_at',
  created_at: 'created_at',
  probability: 'probability',
  activities_total: 'activities_total',
  activities_done: 'activities_done',
  activities_pending: 'activities_pending',
  last_activity_date: 'last_activity_date',
};

// Campos calculados via subquery — só suportados pelo RPC get_deals_page, não por PostgREST direto
const ACTIVITY_SORT_FIELDS = new Set(['activities_total', 'activities_done', 'activities_pending', 'last_activity_date']);

// Campos não mapeáveis pelo RPC nem por uma coluna direta — ordenamos client-side
// após buscar todas as linhas (similar ao caminho de filtros remanescentes).
// `cadence` fica fora: os enrollments são buscados fora do hook (em Deals.tsx),
// então não há dado disponível aqui pra ordenar.
const RELATIONAL_SORT_FIELDS = new Set([
  'lead', 'company', 'owner', 'stage', 'pipeline', 'loss_reason', 'tags', 'status',
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
]);

// Qualquer sort de coluna custom (`cf_*`) ou relacional avançada (`rel_*`) precisa
// rodar client-side — o RPC e o PostgREST direto desconhecem esses paths.
function isClientSideSortField(sortBy: string | null | undefined): boolean {
  if (!sortBy) return false;
  if (RELATIONAL_SORT_FIELDS.has(sortBy)) return true;
  return sortBy.startsWith('cf_') || sortBy.startsWith('rel_');
}

// Extrai o valor comparável de um deal pra um dado sortKey. Suporta:
//   - colunas system de DEALS_COLUMNS
//   - custom fields do próprio deal (`cf_<name>` → deal.custom_data[name])
//   - colunas de entidade vinculada (`rel_<entity>_<field>` p/ system,
//     `rel_<entity>_cd_<name>` p/ custom)
function getDealSortValue(deal: any, sortBy: string): any {
  switch (sortBy) {
    case 'title':              return deal.title;
    case 'value':              return deal.amount;
    case 'contract_total':     return deal.contract_total;
    case 'stage':              return deal.stages?.stage_order;
    case 'close_date':         return deal.close_date;
    case 'won_at':             return deal.won_at;
    case 'lost_at':            return deal.lost_at;
    case 'created_at':         return deal.created_at;
    case 'probability':        return deal.probability;
    case 'activities_total':   return deal.activities_total;
    case 'activities_done':    return deal.activities_done;
    case 'activities_pending': return deal.activities_pending;
    case 'last_activity_date': return deal.last_activity_date;
    case 'lead':               return deal.leads?.name;
    case 'company':            return deal.companies?.name;
    case 'owner':              return deal.owner?.full_name;
    case 'pipeline':           return deal.pipelines?.name;
    case 'loss_reason':        return deal.loss_reasons?.reason_text;
    case 'status':             return deal.status;
    case 'tags':               return (deal.tags || []).length;
    case 'utm_source':         return deal.leads?.utm_source;
    case 'utm_medium':         return deal.leads?.utm_medium;
    case 'utm_campaign':       return deal.leads?.utm_campaign;
    case 'utm_term':           return deal.leads?.utm_term;
    case 'utm_content':        return deal.leads?.utm_content;
  }
  if (sortBy.startsWith('cf_')) {
    return deal.custom_data?.[sortBy.slice(3)];
  }
  // `rel_<entity>_cd_<fieldName>` — custom field da entidade vinculada
  let m = /^rel_(leads|companies)_cd_(.+)$/.exec(sortBy);
  if (m) return deal[m[1]]?.custom_data?.[m[2]];
  // `rel_<entity>_<field>` — coluna nativa da entidade vinculada
  m = /^rel_(leads|companies)_(.+)$/.exec(sortBy);
  if (m) return deal[m[1]]?.[m[2]];
  return undefined;
}

// Comparador único reutilizado pelas duas variantes do hook (useDeals, useDealsInfinite).
// Respeita o tipo do valor: número, data (string ISO), texto. Nulos vão pro fim.
function compareDealsBySortKey(a: any, b: any, sortBy: string): number {
  const va = getDealSortValue(a, sortBy);
  const vb = getDealSortValue(b, sortBy);
  const aNil = va == null || va === '';
  const bNil = vb == null || vb === '';
  if (aNil && bNil) return 0;
  if (aNil) return 1;   // nulos sempre depois
  if (bNil) return -1;
  // Número
  if (typeof va === 'number' && typeof vb === 'number') return va - vb;
  // Data (string ISO-ish)
  if (typeof va === 'string' && typeof vb === 'string') {
    const da = Date.parse(va);
    const db = Date.parse(vb);
    if (!isNaN(da) && !isNaN(db) && /^\d{4}-\d{2}-\d{2}/.test(va) && /^\d{4}-\d{2}-\d{2}/.test(vb)) {
      return da - db;
    }
    return va.localeCompare(vb);
  }
  // Mistura ou outros tipos — coerce pra string
  return String(va).localeCompare(String(vb));
}

/**
 * Busca todas as linhas em lotes quando o filtro exige pós-processamento no cliente.
 * Evita loop infinito se o PostgREST/host devolver sempre a mesma página (range ignorado ou limite fixo).
 */
async function fetchAllDealRowsBatched(
  fetchRange: (from: number, to: number) => PromiseLike<{ data: any[] | null; error: any }>
): Promise<any[]> {
  const BATCH_SIZE = 100;
  const MAX_BATCHES = 2000;
  const seen = new Set<string>();
  const out: any[] = [];
  let offset = 0;

  for (let b = 0; b < MAX_BATCHES; b++) {
    const { data: batch, error } = await fetchRange(offset, offset + BATCH_SIZE - 1);
    if (error) throw error;
    const chunk = batch || [];
    if (chunk.length === 0) break;

    let added = 0;
    for (const row of chunk) {
      const id = row?.id;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(row);
      added++;
    }

    if (chunk.length < BATCH_SIZE) break;
    if (added === 0) {
      console.warn(
        "[useDeals] Lote sem negócios novos (possível range ignorado pelo backend). Interrompendo paginação em lote."
      );
      break;
    }
    offset += BATCH_SIZE;
  }

  return out;
}

export interface UseDealsOptions {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  tagIds?: string[];
  ownerFilterLocal?: string | string[];
  /** Restringe `status` da query (ex.: ['open']). Usado pelo chip de proprietário na Lista. */
  statusFilterLocal?: string[] | null;
  /** Fit tier multi-select; '__none__' = sem dado (fit_tier IS NULL). */
  scores?: string[] | null;
  /** Incrementado ao aplicar filtro na UI — força nova busca mesmo com o mesmo `filterId` na URL. */
  filterApplyRevision?: number;
}

export function useDeals(
  pipelineId?: string,
  filterId?: string,
  recordOwnerFilter?: string,
  options: UseDealsOptions = {}
) {
  const { orgId } = useCurrentOrgId();
  const {
    page = 1,
    pageSize = 50,
    sortBy = 'created_at',
    sortOrder = 'desc',
    tagIds = [],
    ownerFilterLocal,
    statusFilterLocal,
    filterApplyRevision = 0,
  } = options;

  const serverSortField = SERVER_SORT_MAP[sortBy];
  const hasTagFilters = tagIds.length > 0;
  const statusFilter = statusFilterLocal && statusFilterLocal.length > 0 ? statusFilterLocal : null;

  const query = useQuery({
    queryKey: [
      'deals', 'v2-act-sort', orgId, pipelineId, filterId, recordOwnerFilter,
      page, pageSize, sortBy, sortOrder,
      tagIds.join(','), ownerFilterLocal,
      statusFilter ? statusFilter.join(',') : null,
      filterApplyRevision,
    ],
    queryFn: async () => {
      if (!orgId) throw new Error("Organization not found");
      if (!pipelineId) throw new Error("Pipeline é obrigatório para listar negócios");

      const { includeNoOwner, ownerIds: realOwnerIds } = splitOwnerSelection(ownerFilterLocal);
      if (recordOwnerFilter && !realOwnerIds.includes(recordOwnerFilter)) {
        realOwnerIds.push(recordOwnerFilter);
      }

      const filterData = filterId ? await fetchFilterData(filterId, 'deals') : null;
      const cfTypes = await fetchDealCustomFieldTypesMapCached(orgId);

      const cadence = await resolveCadenceFilterFromFilterData(orgId, 'deal', filterData);

      const { hasRemainingClientFilters } = applyDealSavedFilterToQuery(
        supabase.from('deals').select('id').eq('org_id', orgId),
        filterData,
        cfTypes,
        false // list view: não excluir won/lost automaticamente
      );

      // Regra relacional (`companies.*`, `leads.*`) e grupo OR não têm push-down
      // no PostgREST, mas o `build_saved_filter_where` resolve os dois em SQL.
      // Quando o banco cobre 100% das regras, o RPC paginado dá conta — evita
      // baixar a org inteira em lotes de 100 só pra filtrar em memória.
      const serverResolvesWholeFilter = hasRemainingClientFilters
        ? await isSavedFilterFullyServerResolvable(filterId, 'deals')
        : false;

      // Sort por campo relacional / custom field exige client-side: o RPC só
      // ordena por colunas diretas/de atividade; aqui buscamos todas as linhas
      // e ordenamos em memória. Trade-off: pesa em orgs grandes — futura
      // melhoria seria estender o RPC.
      const needsClientSideProcessing =
        (hasRemainingClientFilters && !serverResolvesWholeFilter) ||
        hasTagFilters ||
        isClientSideSortField(sortBy);

      // Caminho default: server-side via RPC (rápido, mesmo embed do PostgREST).
      // Cobre filtro salvo, sort por campo de atividade, e o caso sem filtro
      // nenhum (que antes caía no PostgREST e estourava timeout em orgs grandes).
      if (!needsClientSideProcessing) {
        const [pageRes, countRes] = await Promise.all([
          supabase.rpc('get_deals_page', {
            p_org_id: orgId,
            p_offset: (page - 1) * pageSize,
            p_limit: pageSize,
            p_pipeline_id: shouldApplyDealsListPipelineScope(pipelineId) ? pipelineId : null,
            p_stage_ids: null,
            p_statuses: statusFilter,
            p_search: null,
            p_owner_ids: realOwnerIds.length > 0 ? realOwnerIds : null,
            p_include_no_owner: includeNoOwner,
            p_sort_by: serverSortField || sortBy || 'created_at',
            p_sort_order: sortOrder,
            p_filter_id: filterId ?? null,
            p_match_ids: cadence.matchIds,
            p_exclude_ids: cadence.excludeIds,
          }),
          supabase.rpc('get_deals_count', {
            p_org_id: orgId,
            p_pipeline_id: shouldApplyDealsListPipelineScope(pipelineId) ? pipelineId : null,
            p_stage_ids: null,
            p_statuses: statusFilter,
            p_search: null,
            p_owner_ids: realOwnerIds.length > 0 ? realOwnerIds : null,
            p_include_no_owner: includeNoOwner,
            p_filter_id: filterId ?? null,
            p_match_ids: cadence.matchIds,
            p_exclude_ids: cadence.excludeIds,
          }),
        ]);
        if (pageRes.error) throw pageRes.error;
        if (countRes.error) throw countRes.error;
        const rawDealsList = (Array.isArray(pageRes.data) ? pageRes.data : []) as any[];
        const profilesNeeded = [...new Set(rawDealsList.map((d: any) => d.owner_id).filter(Boolean))] as string[];
        let ownerProfilesMap: Record<string, any> = {};
        if (profilesNeeded.length > 0) {
          const { data: profiles } = await supabase
            .from('profiles')
            .select('user_id, full_name, email, avatar_url')
            .in('user_id', profilesNeeded);
          if (profiles) {
            ownerProfilesMap = profiles.reduce((acc, p) => { acc[p.user_id] = p; return acc; }, {} as Record<string, any>);
          }
        }
        const transformedDeals = rawDealsList.map((deal: any) => ({
          ...deal,
          owner: deal.owner_id ? ownerProfilesMap[deal.owner_id] || null : null,
          tags: deal.deal_tags?.map((dt: any) => dt.tags).filter(Boolean) || [],
        }));
        return { deals: transformedDeals, totalCount: Number(countRes.data ?? 0) };
      }

      // Fallback PostgREST — só usado quando há filtros/tags client-side.
      // Sem `count: 'exact'` para evitar timeout em orgs grandes; total real vem
      // de `transformedDeals.length` após o filtro client-side.
      const buildQuery = (from: number, to: number) => {
        let q = supabase
          .from('deals')
          .select(LIST_VIEW_SELECT)
          .eq('org_id', orgId)
          .order('created_at', { ascending: false });

        const applied = applyDealSavedFilterToQuery(q, filterData, cfTypes, false);
        q = applied.query;

        if (cadence.matchIds) q = q.in('id', cadence.matchIds);
        if (cadence.excludeIds && cadence.excludeIds.length > 0) {
          q = q.not('id', 'in', `(${cadence.excludeIds.join(',')})`);
        }

        if (shouldApplyDealsListPipelineScope(pipelineId)) {
          q = q.eq('pipeline_id', pipelineId);
        }

        if (recordOwnerFilter) q = q.eq('owner_id', recordOwnerFilter);

        q = applyOwnerFilter(q, 'owner_id', ownerFilterLocal);

        return q.range(from, to);
      };

      // Batch-fetch ALL matching rows (filtragem/sort/paginação client-side
      // exigem o universo completo).
      const rawDeals: any[] = await fetchAllDealRowsBatched((from, to) => buildQuery(from, to));
      let totalCount = 0;

      // Batch fetch owner profiles
      const uniqueOwnerIds = [...new Set(
        rawDeals.map((d: any) => d.owner_id).filter(Boolean)
      )] as string[];

      let ownerProfilesMap: Record<string, any> = {};
      if (uniqueOwnerIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, full_name, email, avatar_url')
          .in('user_id', uniqueOwnerIds);

        if (profiles) {
          ownerProfilesMap = profiles.reduce((acc, p) => {
            acc[p.user_id] = p;
            return acc;
          }, {} as Record<string, any>);
        }
      }

      // Transform deals: attach owner profiles + flatten tags
      let transformedDeals = rawDeals.map((deal: any) => ({
        ...deal,
        owner: deal.owner_id ? ownerProfilesMap[deal.owner_id] || null : null,
        tags: deal.deal_tags?.map((dt: any) => dt.tags).filter(Boolean) || [],
      }));

      // --- Client-side filters (only for remaining conditions) ---

      // Apply remaining saved filter conditions (OR groups, custom fields, relation fields)
      if (hasRemainingClientFilters && filterData) {
        try {
          transformedDeals = applyFilterGroups(transformedDeals, filterData, "deals") as any;
        } catch (filterError) {
          console.warn('[useDeals] Client-side filter evaluation failed, showing unfiltered results:', filterError);
        }
      }

      // Apply tag filter
      if (hasTagFilters) {
        transformedDeals = transformedDeals.filter((deal: any) => {
          const dealTagIds = (deal.tags || []).map((t: any) => t?.id || t).filter(Boolean);
          return tagIds.some(tagId => dealTagIds.includes(tagId));
        });
      }

      if (statusFilter) {
        transformedDeals = transformedDeals.filter((d: any) => statusFilter.includes(d.status));
      }

      // Client-side sort cobrindo todos os campos suportados pela UI.
      if (needsClientSideProcessing && sortBy) {
        transformedDeals = [...transformedDeals].sort((a, b) => {
          const cmp = compareDealsBySortKey(a, b, sortBy);
          return sortOrder === 'asc' ? cmp : -cmp;
        });
      }

      // Client-side pagination and accurate total
      if (needsClientSideProcessing) {
        totalCount = transformedDeals.length; // accurate: all rows were fetched and filtered
        const from = (page - 1) * pageSize;
        transformedDeals = transformedDeals.slice(from, from + pageSize);
      }

      return { deals: transformedDeals, totalCount };
    },
    // `__all__` lista todas as pipelines (sem filtro salvo); sem id a query não roda.
    enabled: !!orgId && !!pipelineId,
    // Alinha ao useDealsInfinite/useDealsByStage: query pesada com embed de
    // activities; frescor vem do useCrmRealtime em /deals. Sem refetch-on-focus.
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return {
    data: query.data?.deals ?? [],
    totalCount: query.data?.totalCount ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
  };
}

const DEALS_PAGE_SIZE = 50;

interface DealsPage {
  items: Deal[];
  total: number;
  nextOffset: number | undefined;
}

export function useDealsInfinite(
  pipelineId?: string,
  filterId?: string,
  recordOwnerFilter?: string,
  options: Omit<UseDealsOptions, 'page' | 'pageSize'> = {}
) {
  const { orgId } = useCurrentOrgId();
  const {
    sortBy = 'created_at',
    sortOrder = 'desc',
    tagIds = [],
    ownerFilterLocal,
    statusFilterLocal,
    scores,
    filterApplyRevision = 0,
  } = options;

  const serverSortField = SERVER_SORT_MAP[sortBy];
  const hasTagFilters = tagIds.length > 0;
  const statusFilter = statusFilterLocal && statusFilterLocal.length > 0 ? statusFilterLocal : null;
  const scoreFilter = scores && scores.length > 0 ? scores : null;

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isRefetching,
    isFetching,
    refetch,
  } = useInfiniteQuery<DealsPage, Error, { pages: DealsPage[] }, unknown[], number>({
    queryKey: [
      'deals-infinite', 'v2-act-sort', orgId, pipelineId, filterId, recordOwnerFilter,
      sortBy, sortOrder, tagIds.join(','), ownerFilterLocal,
      statusFilter ? statusFilter.join(',') : null,
      scoreFilter ? scoreFilter.join(',') : null,
      filterApplyRevision,
    ],
    queryFn: async ({ pageParam }) => {
      if (!orgId) throw new Error("Organization not found");
      if (!pipelineId) throw new Error("Pipeline é obrigatório para listar negócios");

      const { includeNoOwner, ownerIds: realOwnerIds } = splitOwnerSelection(ownerFilterLocal);
      if (recordOwnerFilter && !realOwnerIds.includes(recordOwnerFilter)) {
        realOwnerIds.push(recordOwnerFilter);
      }

      const filterData = filterId ? await fetchFilterData(filterId, 'deals') : null;
      const cfTypes = await fetchDealCustomFieldTypesMapCached(orgId);

      const cadence = await resolveCadenceFilterFromFilterData(orgId, 'deal', filterData);

      const { hasRemainingClientFilters } = applyDealSavedFilterToQuery(
        supabase.from('deals').select('id').eq('org_id', orgId),
        filterData,
        cfTypes,
        false // list view: não excluir won/lost automaticamente
      );

      // Veja `useDeals` acima: quando o banco cobre todas as regras do filtro
      // salvo, o RPC paginado substitui o fallback que baixa a org inteira.
      const serverResolvesWholeFilter = hasRemainingClientFilters
        ? await isSavedFilterFullyServerResolvable(filterId, 'deals')
        : false;

      // Veja `useDeals` acima: sort por campo relacional/custom força fetch+sort
      // client-side. Idêntica trade-off de performance pra orgs grandes.
      const needsClientSideProcessing =
        (hasRemainingClientFilters && !serverResolvesWholeFilter) ||
        hasTagFilters ||
        !!scoreFilter ||
        isClientSideSortField(sortBy);

      // Caminho default: server-side via RPC. Cobre filtro salvo, sort por
      // campo de atividade, e o caso sem filtro (que antes caía no PostgREST
      // e estourava timeout em orgs grandes).
      if (!needsClientSideProcessing) {
        const offset = pageParam ?? 0;
        const [pageRes, countRes] = await Promise.all([
          supabase.rpc('get_deals_page', {
            p_org_id: orgId,
            p_offset: offset,
            p_limit: DEALS_PAGE_SIZE,
            p_pipeline_id: shouldApplyDealsListPipelineScope(pipelineId) ? pipelineId : null,
            p_stage_ids: null,
            p_statuses: statusFilter,
            p_search: null,
            p_owner_ids: realOwnerIds.length > 0 ? realOwnerIds : null,
            p_include_no_owner: includeNoOwner,
            p_sort_by: serverSortField || sortBy || 'created_at',
            p_sort_order: sortOrder,
            p_filter_id: filterId ?? null,
            p_match_ids: cadence.matchIds,
            p_exclude_ids: cadence.excludeIds,
          }),
          offset === 0
            ? supabase.rpc('get_deals_count', {
                p_org_id: orgId,
                p_pipeline_id: shouldApplyDealsListPipelineScope(pipelineId) ? pipelineId : null,
                p_stage_ids: null,
                p_statuses: statusFilter,
                p_search: null,
                p_owner_ids: realOwnerIds.length > 0 ? realOwnerIds : null,
                p_include_no_owner: includeNoOwner,
                p_filter_id: filterId ?? null,
                p_match_ids: cadence.matchIds,
                p_exclude_ids: cadence.excludeIds,
              })
            : Promise.resolve({ data: null, error: null }),
        ]);
        if (pageRes.error) throw pageRes.error;
        const rawList = (Array.isArray(pageRes.data) ? pageRes.data : []) as any[];
        const profilesNeeded = [...new Set(rawList.map((d: any) => d.owner_id).filter(Boolean))] as string[];
        let ownerProfilesMap: Record<string, any> = {};
        if (profilesNeeded.length > 0) {
          const { data: profiles } = await supabase
            .from('profiles')
            .select('user_id, full_name, email, avatar_url')
            .in('user_id', profilesNeeded);
          if (profiles) {
            ownerProfilesMap = profiles.reduce((acc, p) => { acc[p.user_id] = p; return acc; }, {} as Record<string, any>);
          }
        }
        const items = rawList.map((deal: any) => ({
          ...deal,
          owner: deal.owner_id ? ownerProfilesMap[deal.owner_id] || null : null,
          tags: deal.deal_tags?.map((dt: any) => dt.tags).filter(Boolean) || [],
        })) as Deal[];
        const total = offset === 0
          ? Number(countRes.data ?? 0)
          : (data?.pages[0]?.total ?? items.length);
        return {
          items,
          total,
          nextOffset: items.length === DEALS_PAGE_SIZE ? offset + DEALS_PAGE_SIZE : undefined,
        };
      }

      // Fallback PostgREST — só usado quando há filtros/tags client-side.
      // Sem `count: 'exact'` para evitar timeout em orgs grandes.
      const buildQuery = (from: number, to: number) => {
        let q = supabase
          .from('deals')
          .select(LIST_VIEW_SELECT)
          .eq('org_id', orgId)
          .order('created_at', { ascending: false });

        const applied = applyDealSavedFilterToQuery(q, filterData, cfTypes, false);
        q = applied.query;

        if (cadence.matchIds) q = q.in('id', cadence.matchIds);
        if (cadence.excludeIds && cadence.excludeIds.length > 0) {
          q = q.not('id', 'in', `(${cadence.excludeIds.join(',')})`);
        }

        if (shouldApplyDealsListPipelineScope(pipelineId)) {
          q = q.eq('pipeline_id', pipelineId);
        }
        if (recordOwnerFilter) q = q.eq('owner_id', recordOwnerFilter);

        if (scoreFilter) {
          const includeNone = scoreFilter.includes('__none__');
          const tiers = scoreFilter.filter((s) => s !== '__none__');
          if (includeNone && tiers.length) q = q.or(`fit_tier.in.(${tiers.join(',')}),fit_tier.is.null`);
          else if (includeNone) q = q.is('fit_tier', null);
          else q = q.in('fit_tier', tiers);
        }

        q = applyOwnerFilter(q, 'owner_id', ownerFilterLocal);

        return q.range(from, to);
      };

      const rawDeals: any[] = await fetchAllDealRowsBatched((from, to) => buildQuery(from, to));

      const uniqueOwnerIds = [...new Set(rawDeals.map((d: any) => d.owner_id).filter(Boolean))] as string[];
      let ownerProfilesMap: Record<string, any> = {};
      if (uniqueOwnerIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, full_name, email, avatar_url')
          .in('user_id', uniqueOwnerIds);
        if (profiles) {
          ownerProfilesMap = profiles.reduce((acc, p) => { acc[p.user_id] = p; return acc; }, {} as Record<string, any>);
        }
      }

      let transformedDeals = rawDeals.map((deal: any) => ({
        ...deal,
        owner: deal.owner_id ? ownerProfilesMap[deal.owner_id] || null : null,
        tags: deal.deal_tags?.map((dt: any) => dt.tags).filter(Boolean) || [],
      }));

      if (hasRemainingClientFilters && filterData) {
        try {
          transformedDeals = applyFilterGroups(transformedDeals, filterData, "deals") as any;
        } catch {
          // show unfiltered on error
        }
      }

      if (hasTagFilters) {
        transformedDeals = transformedDeals.filter((deal: any) => {
          const dealTagIds = (deal.tags || []).map((t: any) => t?.id || t).filter(Boolean);
          return tagIds.some(tagId => dealTagIds.includes(tagId));
        });
      }

      if (statusFilter) {
        transformedDeals = transformedDeals.filter((d: any) => statusFilter.includes(d.status));
      }

      if (sortBy) {
        transformedDeals = [...transformedDeals].sort((a, b) => {
          const cmp = compareDealsBySortKey(a, b, sortBy);
          return sortOrder === 'asc' ? cmp : -cmp;
        });
      }

      // All rows fetched — return as single page (no infinite scrolling when client-side processing needed)
      return {
        items: transformedDeals as Deal[],
        total: transformedDeals.length,
        nextOffset: undefined,
      };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    enabled: !!orgId && !!pipelineId,
    staleTime: 2 * 60 * 1000,
  });

  const deals = useMemo(() => data?.pages.flatMap(p => p.items) || [], [data]);
  const totalCount = data?.pages[0]?.total || 0;

  return {
    deals,
    totalCount,
    isLoading,
    isError: !!error,
    error,
    refetch,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    isRefetching,
    isFetching,
  };
}

/**
 * Fetches ALL deal IDs matching the current filters (lightweight query, no joins).
 * Returns [] if client-side processing is needed (caller should use already-loaded data).
 */
export async function fetchAllFilteredDealIds(
  orgId: string,
  pipelineId?: string,
  recordOwnerFilter?: string,
  filterId?: string,
  tagIds: string[] = [],
  ownerFilterLocal?: string | string[]
): Promise<string[]> {
  const hasTagFilters = tagIds.length > 0;

  // Load saved filter + custom field types (same pattern as useDealsInfinite)
  const filterData = filterId ? await fetchFilterData(filterId, 'deals') : null;
  const cfTypes = await fetchDealCustomFieldTypesMapCached(orgId);

  const cadence = await resolveCadenceFilterFromFilterData(orgId, 'deal', filterData);

  // Probe whether saved filter has client-only rules
  const { hasRemainingClientFilters } = applyDealSavedFilterToQuery(
    supabase.from('deals').select('id').eq('org_id', orgId),
    filterData,
    cfTypes,
    false // list view: não excluir won/lost automaticamente
  );

  // If client-side processing needed, return empty — caller uses already-loaded data
  if (hasRemainingClientFilters || hasTagFilters) {
    return [];
  }

  const BATCH = 100;
  const allIds: string[] = [];
  let offset = 0;

  const buildIdsQuery = () => {
    let q = supabase
      .from('deals')
      .select('id')
      .eq('org_id', orgId);

    // Apply saved filter conditions (server-side rules)
    const applied = applyDealSavedFilterToQuery(q, filterData, cfTypes, false);
    q = applied.query;

    if (cadence.matchIds) q = q.in('id', cadence.matchIds);
    if (cadence.excludeIds && cadence.excludeIds.length > 0) {
      q = q.not('id', 'in', `(${cadence.excludeIds.join(',')})`);
    }

    if (shouldApplyDealsListPipelineScope(pipelineId)) {
      q = q.eq('pipeline_id', pipelineId as string);
    }
    if (recordOwnerFilter) q = q.eq('owner_id', recordOwnerFilter);

    q = applyOwnerFilter(q, 'owner_id', ownerFilterLocal);

    return q;
  };

  while (true) {
    const { data, error } = await buildIdsQuery().range(offset, offset + BATCH - 1);
    if (error) throw error;
    const batch = data || [];
    allIds.push(...batch.map((d: { id: string }) => d.id));
    if (batch.length < BATCH) break;
    offset += BATCH;
  }

  return allIds;
}

export function useDeal(dealId: string | undefined) {
  const { orgId } = useCurrentOrgId();

  return useQuery({
    queryKey: ['deal', dealId, orgId],
    queryFn: async () => {
      if (!orgId || !dealId) throw new Error("Organization or deal not found");

      const { data, error } = await supabase
        .from('deals')
        .select(`
          *,
          leads!deals_lead_id_fkey (
            id,
            name,
            email,
            phone,
            source,
            utm_source,
            utm_medium,
            utm_campaign,
            utm_term,
            utm_content
          ),
          companies (
            id,
            name,
            website,
            industry,
            resumo
          ),
          profiles!deals_created_by_fkey (
            full_name
          ),
          stages (
            id,
            name,
            stage_order,
            probability,
            stage_type
          ),
          deal_tags (
            tag_id,
            tags (id, name, color)
          ),
          loss_reasons (
            id,
            reason_text
          )
        `)
        .eq('id', dealId)
        .eq('org_id', orgId)
        .single();

      if (error) throw error;
      
      // Fetch owner profile if owner_id exists
      let owner = null;
      let owner_inactive = false;
      if (data?.owner_id) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('user_id, full_name, email, avatar_url')
          .eq('user_id', data.owner_id)
          .maybeSingle();

        if (profile) {
          owner = profile;
        }

        // Detecta se o owner ainda tem membership ativa na org atual.
        const { data: membership } = await supabase
          .from('memberships')
          .select('status')
          .eq('user_id', data.owner_id)
          .eq('org_id', orgId)
          .maybeSingle();
        owner_inactive = !membership || membership.status !== 'active';
      }

      // Transform deal_tags to a flat array of tags
      if (data) {
        const tags = data.deal_tags?.map((dt: any) => dt.tags).filter(Boolean) || [];
        return { ...data, tags, owner, owner_inactive } as any;
      }

      return data as any;
    },
    enabled: !!orgId && !!dealId
  });
}

/** Insert direto na tabela `deals` (sem toast nem invalidação). Para lote ou uso fora de `useCreateDeal`. */
export async function insertDealRecord(
  dealData: Omit<Deal, 'id' | 'org_id' | 'created_at' | 'updated_at'>,
  orgId: string
): Promise<Deal> {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error("User not authenticated");

  const row = normalizeDealTimestamptzDateFields({
    ...dealData,
    org_id: orgId,
    created_by: user.user.id,
    // Sem owner_id, a policy restritiva `deals_rep_only_own_select` oculta a
    // linha recém-criada no RETURNING e o insert falha com 403 para reps.
    owner_id: dealData.owner_id ?? user.user.id,
  } as Record<string, unknown>);

  const { data, error } = await supabase
    .from('deals')
    .insert([row] as never)
    .select()
    .single();

  if (error) throw error;
  return data as unknown as Deal;
}

export function useCreateDeal() {
  const queryClient = useQueryClient();
  const { orgId } = useCurrentOrgId();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (dealData: Omit<Deal, 'id' | 'org_id' | 'created_at' | 'updated_at'>) => {
      if (!orgId) throw new Error("Organization not found");
      return insertDealRecord(dealData, orgId);
    },
    onSuccess: () => {
      // Invalidate all deals queries for this organization, including those filtered by pipeline
      queryClient.invalidateQueries({ queryKey: ['deals', orgId] });
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['deals-by-stage'] });
      queryClient.invalidateQueries({ queryKey: ['deal-stage-counts'] });
      queryClient.invalidateQueries({ queryKey: ['deal-events'] });
      toast({
        title: "Negócio criado!",
        description: "O negócio foi criado com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao criar negócio",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useUpdateDeal() {
  const queryClient = useQueryClient();
  const { orgId } = useCurrentOrgId();
  const { toast } = useToast();

  return useMutation({
    // O default do queryClient é `retry: 1`. Para gravação de negócio isso é ruim:
    // quando o statement morre no `statement_timeout` de 8s, a retentativa bate na
    // mesma cascata de triggers contendida (dobra a carga e a espera do rep para
    // ~16s) e, no caso de commit com resposta perdida, reaplica um UPDATE já
    // aplicado. Só vale retentar falha de rede — aí o request nem chegou ao banco.
    retry: (failureCount, error) =>
      failureCount < 1 && error instanceof TypeError && /failed to fetch/i.test(error.message),
    mutationFn: async ({ id, ...dealData }: Partial<Deal> & { id: string }) => {
      // Buscar o deal atual antes de atualizar para saber o stage_id anterior
      const { data: currentDeal } = await supabase
        .from('deals')
        .select('stage_id, pipeline_id, status, lead_id, company_id, owner_id')
        .eq('id', id)
        .eq('org_id', orgId)
        .single();

      const normalizedPayload = normalizeDealTimestamptzDateFields(
        dealData as Record<string, unknown>,
      );

      // A policy RESTRICTIVE `deals_rep_only_own_select` esconde a linha no
      // RETURNING quando um rep reatribui o deal para outro usuário
      // (owner_id != auth.uid() e sdr_responsavel != auth.uid()), fazendo o
      // `.select()` falhar com "new row violates row-level security policy" —
      // mesmo com o UPDATE em si autorizado. Nesse caso gravamos sem RETURNING
      // e sintetizamos o retorno (onSuccess só lê campos escalares, nunca
      // relações aninhadas). Setar owner para null ou para si mesmo continua
      // visível pela policy, então mantém o caminho normal. O bloco de
      // tratamento de erro abaixo serve de rede de segurança para casos que
      // este heurístico não cobrir.
      const reassigningAway =
        'owner_id' in normalizedPayload &&
        normalizedPayload.owner_id != null &&
        normalizedPayload.owner_id !== (currentDeal?.owner_id ?? null);

      if (reassigningAway) {
        const { error } = await supabase
          .from('deals')
          .update(normalizedPayload)
          .eq('id', id)
          .eq('org_id', orgId);

        if (error) throw error;

        return {
          ...currentDeal,
          ...normalizedPayload,
          id,
          previousStageId: currentDeal?.stage_id,
          previousPipelineId: currentDeal?.pipeline_id,
          pipelineId: (normalizedPayload.pipeline_id as string) || currentDeal?.pipeline_id,
          previousStatus: (currentDeal as { status?: string } | null)?.status,
        } as unknown as Deal & {
          previousStageId?: string;
          previousPipelineId?: string;
          pipelineId?: string;
          previousStatus?: string;
        };
      }

      const { data, error } = await supabase
        .from('deals')
        .update(normalizedPayload)
        .eq('id', id)
        .eq('org_id', orgId)
        // RETURNING sem embeds: o UPDATE de deals dispara ~15 triggers AFTER
        // (cancelar atividades/cadências, automações, webhooks) e tudo isso cabe
        // no mesmo `statement_timeout` de 8s do role `authenticated`. Os LATERAL
        // joins de leads/companies/profiles só encareciam o statement — `onSuccess`
        // (e todos os callers) leem apenas campos escalares do próprio deal.
        .select('*')
        .single();

      if (error) {
        // Rede de segurança: a policy RESTRICTIVE `deals_rep_only_own_select`
        // pode esconder a linha pós-update no RETURNING (ex.: reps reatribuindo
        // um deal cuja visibilidade muda), fazendo o `.select()` falhar com
        // 42501 "new row violates row-level security policy" — embora o UPDATE
        // em si tenha sido aplicado. Quando o heurístico `reassigningAway` não
        // pega o caso, refazemos sem RETURNING e sintetizamos o retorno
        // (onSuccess só lê campos escalares, nunca relações aninhadas).
        const isRlsReturningError =
          (error as { code?: string }).code === '42501' ||
          /row-level security/i.test((error as { message?: string }).message ?? '');

        if (!isRlsReturningError) throw error;

        const { error: retryError } = await supabase
          .from('deals')
          .update(normalizedPayload)
          .eq('id', id)
          .eq('org_id', orgId);

        if (retryError) throw retryError;

        return {
          ...currentDeal,
          ...normalizedPayload,
          id,
          previousStageId: currentDeal?.stage_id,
          previousPipelineId: currentDeal?.pipeline_id,
          pipelineId: (normalizedPayload.pipeline_id as string) || currentDeal?.pipeline_id,
          previousStatus: (currentDeal as { status?: string } | null)?.status,
          previousOwnerId: currentDeal?.owner_id,
        } as unknown as Deal & {
          previousStageId?: string;
          previousPipelineId?: string;
          pipelineId?: string;
          previousStatus?: string;
          previousOwnerId?: string;
        };
      }

      // Retornar também o stage_id e pipeline_id anterior para invalidar queries (antiga e nova pipeline)
      return {
        ...data,
        previousStageId: currentDeal?.stage_id,
        previousPipelineId: currentDeal?.pipeline_id,
        pipelineId: currentDeal?.pipeline_id || data.pipeline_id,
        previousStatus: (currentDeal as { status?: string } | null)?.status,
        previousOwnerId: currentDeal?.owner_id,
      };
    },
    onMutate: async (variables) => {
      const { id, stage_id: newStageId } = variables;

      // Only do optimistic update for stage changes
      if (!newStageId) return {};

      // Cancel outgoing refetches for relevant queries
      await queryClient.cancelQueries({ queryKey: ['deals-by-stage', orgId] });
      await queryClient.cancelQueries({ queryKey: ['deal-stage-counts', orgId] });
      await queryClient.cancelQueries({ queryKey: ['deals-count-by-stage', orgId] });

      // Search cache for the deal to get current data and old stage
      const queryCache = queryClient.getQueryCache();
      const dealsByStageQueries = queryCache.findAll({ queryKey: ['deals-by-stage', orgId] });

      let dealToMove: Deal | null = null;
      let oldStageId: string | null = null;
      let dealAmount = 0;

      for (const query of dealsByStageQueries) {
        const data = query.state.data as { deals: Deal[]; total: number } | undefined;
        if (data?.deals) {
          const found = data.deals.find((d: Deal) => d.id === id);
          if (found) {
            dealToMove = {
              ...found,
              stage_id: newStageId,
              ...(variables.probability !== undefined ? { probability: variables.probability } : {}),
              ...(variables.loss_reason_id !== undefined ? { loss_reason_id: variables.loss_reason_id } : {})
            };
            oldStageId = found.stage_id;
            dealAmount = found.amount || 0;
            break;
          }
        }
      }

      // If deal not found in cache or no actual stage change, skip optimistic update
      if (!dealToMove || !oldStageId || oldStageId === newStageId) return {};

      // Save snapshots for rollback
      const previousDealsByStage: { queryKey: readonly unknown[]; data: unknown }[] = [];
      for (const query of dealsByStageQueries) {
        const stageId = query.queryKey[3];
        if (stageId === oldStageId || stageId === newStageId) {
          previousDealsByStage.push({
            queryKey: query.queryKey,
            data: query.state.data
          });
        }
      }

      const stageCountsQueries = queryCache.findAll({ queryKey: ['deal-stage-counts', orgId] });
      const previousStageCounts: { queryKey: readonly unknown[]; data: unknown }[] = [];
      for (const query of stageCountsQueries) {
        previousStageCounts.push({ queryKey: query.queryKey, data: query.state.data });
      }

      const dealsCountQueries = queryCache.findAll({ queryKey: ['deals-count-by-stage', orgId] });
      const previousDealsCount: { queryKey: readonly unknown[]; data: unknown }[] = [];
      for (const query of dealsCountQueries) {
        previousDealsCount.push({ queryKey: query.queryKey, data: query.state.data });
      }

      // Optimistically remove deal from old stage queries (all pages)
      for (const query of dealsByStageQueries) {
        const stageId = query.queryKey[3];
        if (stageId === oldStageId) {
          queryClient.setQueryData(query.queryKey, (old: any) => {
            if (!old?.deals) return old;
            if (!old.deals.some((d: any) => d.id === id)) return old;
            return {
              ...old,
              deals: old.deals.filter((d: any) => d.id !== id),
              total: Math.max(0, (old.total || 1) - 1)
            };
          });
        }
      }

      // Optimistically add deal to new stage (page 1 query)
      for (const query of dealsByStageQueries) {
        const stageId = query.queryKey[3];
        const page = query.queryKey[5];
        if (stageId === newStageId && page === 1) {
          queryClient.setQueryData(query.queryKey, (old: any) => {
            if (!old) return { deals: [dealToMove], total: 1 };
            return {
              ...old,
              deals: [dealToMove, ...old.deals.filter((d: any) => d.id !== id)],
              total: (old.total || 0) + 1
            };
          });
        }
      }

      // Optimistically update deal-stage-counts
      for (const query of stageCountsQueries) {
        queryClient.setQueryData(query.queryKey, (old: any) => {
          if (!old) return old;
          const updated = { ...old };
          if (updated[oldStageId!]) {
            updated[oldStageId!] = {
              count: Math.max(0, updated[oldStageId!].count - 1),
              totalAmount: Math.max(0, updated[oldStageId!].totalAmount - dealAmount)
            };
          }
          if (updated[newStageId]) {
            updated[newStageId] = {
              count: (updated[newStageId].count || 0) + 1,
              totalAmount: (updated[newStageId].totalAmount || 0) + dealAmount
            };
          } else {
            updated[newStageId] = { count: 1, totalAmount: dealAmount };
          }
          return updated;
        });
      }

      // Optimistically update deals-count-by-stage
      for (const query of dealsCountQueries) {
        queryClient.setQueryData(query.queryKey, (old: any) => {
          if (!old) return old;
          const updated = { ...old };
          if (updated[oldStageId!]) {
            updated[oldStageId!] = {
              count: Math.max(0, updated[oldStageId!].count - 1),
              totalAmount: Math.max(0, updated[oldStageId!].totalAmount - dealAmount)
            };
          }
          if (updated[newStageId]) {
            updated[newStageId] = {
              count: (updated[newStageId].count || 0) + 1,
              totalAmount: (updated[newStageId].totalAmount || 0) + dealAmount
            };
          } else {
            updated[newStageId] = { count: 1, totalAmount: dealAmount };
          }
          return updated;
        });
      }

      return { previousDealsByStage, previousStageCounts, previousDealsCount };
    },
    onSuccess: async (data) => {
      // Type assertion para incluir propriedades adicionais
      const dealData = data as Deal & {
        previousStageId?: string;
        previousPipelineId?: string;
        pipelineId?: string;
        previousStatus?: string;
        previousOwnerId?: string | null;
      };

      if (
        orgId &&
        dealData.previousStatus !== "won" &&
        dealData.status === "won"
      ) {
        try {
          await maybeOpenScheduleAfterDealWon({
            orgId,
            dealId: dealData.id,
            leadId: dealData.lead_id,
            companyId: dealData.company_id,
            ownerId: dealData.owner_id ?? null,
          });
        } catch (e) {
          console.warn("[useUpdateDeal] schedule after won", e);
        }
      }

      if (dealData.lead_id) {
        try {
          if (dealData.status === 'won') {
            const { error: leadError } = await supabase
              .from('leads')
              .update({ status: 'won' })
              .eq('id', dealData.lead_id)
              .eq('org_id', orgId);

            if (!leadError) {
              queryClient.invalidateQueries({ queryKey: ['leads', orgId] });
              queryClient.invalidateQueries({ queryKey: ['lead', dealData.lead_id] });
            }
          } else if (dealData.status === 'lost') {
            const leadUpdate: { status: string; loss_reason_id?: string | null; custom_loss_reason?: string | null } = { status: 'lost' };
            if (dealData.loss_reason_id != null) leadUpdate.loss_reason_id = dealData.loss_reason_id;
            if (dealData.custom_loss_reason != null) leadUpdate.custom_loss_reason = dealData.custom_loss_reason;
            const { error: leadError } = await supabase
              .from('leads')
              .update(leadUpdate as never)
              .eq('id', dealData.lead_id)
              .eq('org_id', orgId);

            if (!leadError) {
              queryClient.invalidateQueries({ queryKey: ['leads', orgId] });
              queryClient.invalidateQueries({ queryKey: ['lead', dealData.lead_id] });
            }
          }
        } catch (error) {
          console.error('❌ Erro ao atualizar lead:', error);
        }
      }

      // Invalidate all deals queries to ensure Kanban and other views update
      queryClient.invalidateQueries({ queryKey: ['deal', dealData.id, orgId] });
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['deals', orgId] });
      queryClient.invalidateQueries({ queryKey: ['deal', dealData.id] });
      queryClient.invalidateQueries({ queryKey: ['deal-events', dealData.id] });

      // Trocar o dono do deal reatribui (via trigger no banco) as atividades
      // pending/paused do deal ao novo dono. Invalidar para refletir na hora
      // (painel de atividades do deal + página Atividades).
      if (dealData.previousOwnerId !== dealData.owner_id) {
        queryClient.invalidateQueries({ queryKey: ['activities'] });
      }

      // Se o stage_id mudou, invalidar as queries deals-by-stage para etapa antiga e nova
      // Se a pipeline mudou (ex.: transferência), invalidar pipeline antiga e nova
      const pipelineChanged = dealData.previousPipelineId && dealData.previousPipelineId !== dealData.pipeline_id;
      const stageChanged = dealData.previousStageId && dealData.previousStageId !== dealData.stage_id;

      if (pipelineChanged) {
        // Transferência de pipeline: remover da pipeline antiga e aparecer na nova
        console.log(`[Kanban] 🚚 Pipeline alterada - Deal ID: ${dealData.id}, Pipeline anterior: ${dealData.previousPipelineId}, Nova pipeline: ${dealData.pipeline_id}`);
        queryClient.invalidateQueries({ queryKey: ['deals-by-stage', orgId, dealData.previousPipelineId, dealData.previousStageId], exact: false });
        queryClient.invalidateQueries({ queryKey: ['deals-count-by-stage', orgId, dealData.previousPipelineId], exact: false });
        queryClient.invalidateQueries({ queryKey: ['deal-stage-counts', orgId, dealData.previousPipelineId], exact: false });
        queryClient.invalidateQueries({ queryKey: ['deals-by-stage', orgId, dealData.pipeline_id, dealData.stage_id], exact: false });
        queryClient.invalidateQueries({ queryKey: ['deals-count-by-stage', orgId, dealData.pipeline_id], exact: false });
        queryClient.invalidateQueries({ queryKey: ['deal-stage-counts', orgId, dealData.pipeline_id], exact: false });
        queryClient.invalidateQueries({ queryKey: ['pipeline', dealData.previousPipelineId] });
        queryClient.invalidateQueries({ queryKey: ['pipeline', dealData.pipeline_id] });
        queryClient.invalidateQueries({ queryKey: ['pipelines', orgId] });
      } else if (stageChanged && dealData.pipelineId) {
        // Mesma pipeline, etapa alterada
        console.log(`[Kanban] 🚚 Card movido - Deal ID: ${dealData.id}, Stage anterior: ${dealData.previousStageId}, Novo stage: ${dealData.stage_id}, Pipeline: ${dealData.pipelineId}`);
        queryClient.invalidateQueries({ queryKey: ['deals-by-stage', orgId, dealData.pipelineId, dealData.previousStageId], exact: false });
        queryClient.invalidateQueries({ queryKey: ['deals-by-stage', orgId, dealData.pipelineId, dealData.stage_id], exact: false });
        queryClient.invalidateQueries({ queryKey: ['deals-count-by-stage', orgId, dealData.pipelineId], exact: false });
        queryClient.invalidateQueries({ queryKey: ['deal-stage-counts', orgId, dealData.pipelineId], exact: false });
      } else if (dealData.stage_id && dealData.pipeline_id) {
        // Mesmo que não tenha mudado de etapa, invalidar a etapa atual para garantir atualização
        console.log(`[Kanban] ✏️ Card atualizado (mesma etapa) - Deal ID: ${dealData.id}, Stage: ${dealData.stage_id}, Pipeline: ${dealData.pipeline_id}`);
        queryClient.invalidateQueries({ queryKey: ['deals-by-stage', orgId, dealData.pipeline_id, dealData.stage_id], exact: false });
      }
      
      toast({
        title: "Negócio atualizado!",
        description: "O negócio foi atualizado com sucesso.",
      });
    },
    onError: (error, _variables, context: any) => {
      // Rollback optimistic updates
      if (context?.previousDealsByStage) {
        for (const { queryKey, data } of context.previousDealsByStage) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (context?.previousStageCounts) {
        for (const { queryKey, data } of context.previousStageCounts) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (context?.previousDealsCount) {
        for (const { queryKey, data } of context.previousDealsCount) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      // O `onError` da mutation SOBRESCREVE o `onError` default do queryClient
      // (que reporta ao Sentry). Sem este capture, falha de update de negócio
      // ficava invisível — foi o que impediu diagnosticar o "Erro ao mover
      // negócio" em produção.
      Sentry.captureException(error, { tags: { source: 'useUpdateDeal' } });
      console.error('[useUpdateDeal] falha ao atualizar negócio:', error);

      toast({
        title: "Erro ao atualizar negócio",
        description: describeDealUpdateError(error),
        variant: "destructive",
      });
    },
  });
}

export function useDeleteDeal() {
  const queryClient = useQueryClient();
  const { orgId } = useCurrentOrgId();
  const { toast } = useToast();

  // Invalida tanto a lista paginada quanto os counts: o badge da etapa é
  // fonte de verdade pro `hasMore` em useDealsByStage, então uma stale
  // count vira bug de paginação visível.
  const invalidateDealQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['deals', orgId] });
    queryClient.invalidateQueries({ queryKey: ['deal-events'] });
    queryClient.invalidateQueries({ queryKey: ['deals-by-stage'] });
    queryClient.invalidateQueries({ queryKey: ['deal-stage-counts'] });
  };

  // Restaura um deal soft-deletado voltando ao status anterior à exclusão. O
  // trigger `a_deals_soft_delete_stamp` limpa `deleted_at` automaticamente.
  const restoreDeal = async (dealId: string, previousStatus: string) => {
    const { error } = await supabase
      .from('deals')
      .update({ status: previousStatus } as never)
      .eq('id', dealId)
      .eq('org_id', orgId);

    if (error) {
      toast({
        title: "Erro ao restaurar negócio",
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    invalidateDealQueries();
    toast({
      title: "Negócio restaurado",
      description: "O negócio foi restaurado com sucesso.",
    });
  };

  return useMutation({
    mutationFn: async (dealId: string) => {
      // Soft delete: marca status='deleted' em vez de apagar a linha. O deal
      // fica recuperável por 30 dias (job `purge-soft-deleted-deals` faz o hard
      // delete depois); o trigger carimba `deleted_at` e audita o evento. Busca
      // o status atual antes para permitir desfazer restaurando o valor anterior.
      const { data: current } = await supabase
        .from('deals')
        .select('status')
        .eq('id', dealId)
        .eq('org_id', orgId)
        .single();

      const previousStatus = (current?.status as string | undefined) ?? 'open';

      const { error } = await supabase
        .from('deals')
        .update({ status: 'deleted' } as never)
        .eq('id', dealId)
        .eq('org_id', orgId);

      if (error) throw error;
      return { dealId, previousStatus };
    },
    onSuccess: ({ dealId, previousStatus }) => {
      invalidateDealQueries();
      toast({
        title: "Negócio excluído!",
        description: "Você tem 30 dias para restaurá-lo.",
        // Padrão de 1s do toaster é curto demais para clicar em Desfazer.
        duration: 8000,
        action: createElement(
          ToastAction,
          {
            altText: "Desfazer exclusão do negócio",
            onClick: () => {
              void restoreDeal(dealId, previousStatus);
            },
          },
          "Desfazer",
        ),
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao excluir negócio",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}
